import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootReducerType, AppDispatch, RootState } from "@store/store";
import { paymentThunk, postPayloadBkpayThunk } from "@store/thunk/PaymentThunk";
import {
  FeesDetailsThunk,
  fetchApplicationDetails,
  getIsCheckApplicationSubmittedThunk,
} from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import { cleanDigitSectionValue } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";
import PaymentLoader from "../../../utils/PaymentLoader";
import { toast } from "react-toastify";
import ConfirmDialog from "@components/Modal/ConfirmModal";

interface PaymentSectionProps {
  isProcessing: boolean;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({ isProcessing }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { access_id, district, state, address_1, pincode } = useSelector(
    (state: RootReducerType) => state.amenities,
  );
  // const [processF]

  const { applicationDetails } = useSelector(
    (state: RootState) => state.applicationDetails,
  );

  useEffect(() => {
    dispatch(fetchApplicationDetails(access_id));
  }, [dispatch, access_id]);

  const { profile } = useSelector((state: RootReducerType) => state.profile);
  // ✅ feesDetails from Redux
  const { feesDetails, loading } = useSelector(
    (state: any) => state.feesDetails,
  );

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  // ✅ Fetch feesDetails on load
  useEffect(() => {
    if (access_id) {
      dispatch(FeesDetailsThunk({ applicationId: access_id }));
    }
  }, [access_id, dispatch]);

  const handlePostBase64 = async () => {
    try {
      const fullName = profile?.name || "";
      const nameParts = fullName
        .trim()
        .split(" ")
        .filter((part) => part.length > 0);
      const firstName = nameParts?.length > 0 ? nameParts?.[0] : "";
      const lastName =
        nameParts?.length > 1 ? nameParts?.slice(1).join(" ") : "";
      let mobileNumber = profile?.mobile_number || "";
      if (mobileNumber.startsWith("+91")) {
        mobileNumber = mobileNumber.slice(3);
      }
      const bkPayload = {
        application_id: access_id,
        FirstName: firstName || "",
        LastName: lastName || "",
        Address1: profile?.address || "",
        Address2: "",
        PostalCode: profile?.pincode || "",
        City: profile?.district || "",
        StateRegion: profile?.district || "",
        State: profile?.state || "",
        CountryCode: "INDIA",
        MobileNumber: mobileNumber,
        payment_stage: 1,
      };
      const getSignedBase64: any = await dispatch(
        postPayloadBkpayThunk(bkPayload),
      ).unwrap();
      if (getSignedBase64.success === false) {
        toast.error(
          getSignedBase64.message ||
            "Failed to initiate payment. Please try again.",
        );
        setShowLoader(false);
        return;
      }
      const form = document.createElement("form");
      form.method = "POST";
      form.action =
        process.env.REACT_APP_BHARATKOSH_URL || "https://bharatkosh.gov.in/";

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "bharrkkosh";
      input.value = getSignedBase64;

      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      setShowLoader(false);
      console.error("Error posting base64:", error);
    }
  };

  const handlePayment = async () => {
    if (!access_id) return;
    if (feesDetails?.processingFees === 0) {
      try {
        const paymentDoneResponse: any = await dispatch(
          paymentThunk(access_id),
        ).unwrap();
        const applicationRes = await dispatch(
          fetchApplicationDetails(access_id),
        ).unwrap();

        if (paymentDoneResponse) {
          const applicationSubmitted = await dispatch(
            getIsCheckApplicationSubmittedThunk(access_id),
          ).unwrap();
          setTimeout(() => {
            navigate(
              `/application-submitted?id=${applicationRes?.application_code}`,
              { replace: true },
            );
          }, 1000);
        }
      } catch (error) {
        console.error("Error in payment done thunk:", error);
      }
    } else {
      setShowLoader(true);
      await handlePostBase64();
    }
    return;
  };

  useEffect(() => {
    if (isProcessing) {
      const timer = setTimeout(() => {
        setPaymentSuccess(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isProcessing]);

  useEffect(() => {
    if (paymentSuccess) {
      const redirectTimer = setTimeout(() => {
        navigate("/application-summary");
      }, 3000);
      return () => clearTimeout(redirectTimer);
    }
  }, [paymentSuccess, navigate]);

  return (
    <>
      <PaymentLoader open={showLoader} />
      <Box sx={{ py: 1, minHeight: "80vh", width: "97%", margin: "0 auto" }}>
        <Typography
          variant="h6"
          fontWeight={700}
          gutterBottom
          sx={{ fontSize: "20px" }}
        >
          Payment
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Alert severity="error" sx={{ mb: 2, fontSize: "14px" }}>
          <strong>Important:</strong> Please wait for 20 minutes if you have
          recently attempted the payment. If the amount has been deducted but an
          Application ID was not generated, contact our support team at{" "}
          <a
            href="mailto: support-rmp@gov.in"
            style={{ textDecoration: "underline" }}
          >
            {" "}
            support-rmp@gov.in
          </a>{" "}
          for assistance.
        </Alert>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {/* Further Instructions Section */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              border: "1px solid #e0e0e0",
              borderRadius: "12px",
              backgroundColor: "#fff",
              minWidth: 300,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize="18px"
              color="text.primary"
              gutterBottom
            >
              Further Instructions
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
              1. Upon approval of the Application, the Applicant shall be under
              a legal obligation to furnish a Bank Guarantee in the sum of INR
              2,50,000 (Rupees Two Lakh Fifty Thousand only) in favour of the
              Competent Authority, in such form and manner as prescribed under
              the applicable rules and guidelines of the Ministry of Road
              Transport & Highways (MoRTH). The final fee shall be determined
              after considering the applicable 5% annual increment effective
              from 26/06/2020, in accordance with the prevailing rules and
              guidelines.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
              2. In the event of rejection of the Application, the Applicant
              shall be required to remit afresh the non-refundable Processing
              Fee of INR 10,000 (Rupees Ten Thousand only).
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
              3. The consideration, examination, and approval of the Application
              shall be subject in all respects to, and governed strictly by, the
              MoRTH Guidelines.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "14px" }} paragraph>
              4. The review and evaluation of the Application shall commence
              only upon successful submission with payment confirmation.
            </Typography>
          </Box>

          {/* Payment Details Section */}
          <Box
            sx={{
              width: 360,
              p: 3,
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              mb={2}
              color="#3A1C73"
              fontSize="18px"
            >
              Payment Details
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Applicant Name:</strong>{" "}
                {profile?.name || "Rahul Sharma"}
              </Typography>
              <Typography variant="body2">
                <strong>Amenity Name:</strong>{" "}
                {applicationDetails?.application_category || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Stretch Reach Type:</strong>{" "}
                {applicationDetails?.location_type_name || "-"}
              </Typography>
              {/* <Typography variant="body2" mb={1}>
                            <strong>Location Address:</strong> {address_1 || "14, noida sector 16, UP"}
                        </Typography> */}
              <Typography variant="body2" mb={1}>
                <strong>Location Address:</strong>{" "}
                {[
                  applicationDetails?.address_1,
                  applicationDetails?.address_2,
                  applicationDetails?.district,
                  applicationDetails?.state,
                ]
                  .filter(Boolean) // remove null/undefined/empty
                  .join(", ")}
              </Typography>
            </Box>

            <Box sx={{ borderBottom: "1px dashed #bbb", mb: 2 }} />

            {/* Application Processing Fee */}
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">
                Application Processing Fee
              </Typography>
              <Typography variant="body2">
                Rs.{" "}
                {feesDetails?.processingFees?.toLocaleString("en-IN") || "0.00"}
              </Typography>
            </Box>

            {/* Conditionally render Penalty Fee */}
            {feesDetails?.penaltyFees ? (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Penalty Fee</Typography>
                <Typography variant="body2">
                  Rs. {feesDetails.penaltyFees.toLocaleString("en-IN")}
                </Typography>
              </Box>
            ) : null}

            <Box sx={{ borderBottom: "1px dashed #bbb", my: 2 }} />

            {/* Total calculation */}
            <Box display="flex" justifyContent="space-between" mb={3}>
              <Typography variant="body1" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                Rs.{" "}
                {feesDetails?.penaltyFees
                  ? (
                      (feesDetails?.processingFees || 0) +
                      (feesDetails?.penaltyFees || 0)
                    ).toLocaleString("en-IN")
                  : feesDetails?.processingFees?.toLocaleString("en-IN") ||
                    "0.00"}
              </Typography>
            </Box>
            {feesDetails?.processingFees === 0 ? (
              <Button
                variant="contained"
                fullWidth
                onClick={() => setOpenConfirm(true)}
                sx={{
                  borderRadius: "24px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#3A1C73",
                  padding: "12px",
                  fontSize: "16px",
                  height: "40px",
                  "&:hover": {
                    backgroundColor: "#2a145c",
                  },
                }}
              >
                Submit Application
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={() => setOpenConfirm(true)}
                disabled={showLoader}
                sx={{
                  borderRadius: "24px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: "#3A1C73",
                  padding: "12px",
                  fontSize: "16px",
                  height: "40px",
                  "&:hover": {
                    backgroundColor: "#2a145c",
                  },
                }}
              >
                Review and Pay Now
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <ConfirmDialog
        open={openConfirm}
        message={
          <>
            <Typography mb={2} fontSize={14} fontWeight="bold">
              Please confirm that you understand and wish to proceed with
              submission.
            </Typography>

            <Typography
              fontWeight="bold"
              mb={1}
              sx={{ textDecoration: "underline" }}
            >
              Important:
            </Typography>

            <Typography mb={1} fontSize={12}>
              All subsequent payments related to this application must be made
              only through the Rajmarg Pravesh Portal. Direct payment through
              BharatKosh or payment through any offline mode is strictly not
              permitted.
            </Typography>

            <Typography fontSize={12}>
              No refund, adjustment, reimbursement, or relaxation will be
              considered for payments made outside the portal.
            </Typography>
          </>
        }
        title="Disclaimer"
        confirmText={
          feesDetails?.processingFees === 0
            ? "Yes, Submit Application"
            : "Yes, Pay Now"
        }
        cancelText="Cancel"
        onConfirm={() => {
          setOpenConfirm(false);
          handlePayment();
        }}
        onClose={() => setOpenConfirm(false)}
      />
    </>
  );
};

export default PaymentSection;
